import Head from 'next/head';
import { NextSeo } from 'next-seo';
import PaymentFlow from '@cont/Checkout/PaymentFlow';
import { PaymentMethodsContextProvider } from '@context/PaymentMethodsContext';
import PaymentPageLayout from '@layout/PaymentLayout/PaymentPageLayout';
import {
  fetchPurchaseDetails,
  getPurchaseDetailsEndpoint
} from '@api/fetchers/checkout';
import { SWRConfig } from 'swr';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

const PaymentPage = ({ purchaseDetails = null }) => {
  const router = useRouter();
  const { subscriptionUID, source, goalSlug, goalUID } = router.query;

  const goalName = purchaseDetails?.subscription?.goal?.name;
  const metaTitle = `${goalName} – Subscribe`;
  const metaDescription = `Subscribe to ${goalName}, valid for 12 months. Sharpen your exams preparation with an expert-designed test series.`;
  const canonicalURL =
    goalSlug && goalUID && subscriptionUID
      ? `https://unacademy.com/goal/${goalSlug}/${goalUID}/subscribe/${subscriptionUID}`
      : null;

  useEffect(() => {
    if (source === 'app') {
      localStorage.setItem('isAppSource', 'true');
    }
  }, [source]);

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalURL
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <SWRConfig
        value={{
          fallback: purchaseDetails
            ? {
                [getPurchaseDetailsEndpoint(subscriptionUID)]: purchaseDetails
              }
            : {}
        }}
      >
        <PaymentMethodsContextProvider>
          <PaymentFlow />
        </PaymentMethodsContextProvider>
      </SWRConfig>
    </>
  );
};

PaymentPage.pageInfo = {
  isMWebSupported: true,
  name: 'Payment Page',
  layoutOptions: {
    disableDarkMode: true,
    hideIntercom: true,
    hideFreshbot: true
  }
};

PaymentPage.Layout = PaymentPageLayout;

// CDN cache with a TTL of 4hrs is enabled for this page
export const getStaticProps = async ({ params: query }) => {
  const { subscriptionUID } = query;

  try {
    const purchaseDetails = await fetchPurchaseDetails(subscriptionUID);
    if (purchaseDetails?.subscription) {
      return { props: { purchaseDetails }, revalidate: 1 * 60 };
    }
    return { props: { purchaseDetails: null }, revalidate: 1 * 60 };
  } catch (err) {
    return { props: { purchaseDetails: null }, revalidate: 1 * 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default PaymentPage;
